import { Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { SideNav } from '@/components/shared/side-nav'
import { CommandPalette } from '@/components/shared/command-palette'
import { StageManagerDialog } from '@/components/shared/stage-manager-dialog'
import { StageFieldsDialog } from '@/components/shared/stage-fields-dialog'
import { SectionSubnav } from '@/components/shared/section-subnav'
import { navForRole, sectionForPath, subnavPageCount } from '@/components/shared/nav-config'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { ImpersonationBanner } from '@/pages/conversations/view-as-staff'
import { NotificationCenter } from '@/components/shared/notification-center'
import { ReleaseNoteDialog } from '@/components/shared/release-note-dialog'
import { SessionExpiredModal } from '@/components/shared/session-expired-modal'
import { OnboardingBackBar, OnboardingResumeBar } from '@/components/shared/onboarding-entry'
import { WelcomeDialog } from '@/components/shared/welcome-dialog'
import { EntryPrompts } from '@/components/shared/entry-prompts'
import { InstallGuideDialog } from '@/components/shared/install-app'
import { MobileTabBar } from '@/components/shared/mobile-tab-bar'
import { MobileSectionTabs } from '@/components/shared/mobile-section-tabs'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { isChatDetailPath, isMobileFullBleedPath } from '@/lib/mobile-nav'
import { useKeyboardViewport } from '@/hooks/use-keyboard-viewport'
import { desktopOnlyFor, mobileNav } from '@/lib/mobile-scope'
import { DesktopOnlyNotice } from '@/components/shared/desktop-only-notice'
import { useAccountStatusRealtime } from '@/hooks/use-account-status-realtime'
import { FeedbackBubble } from '@/components/shared/feedback-bubble'
import { PendingOrderBar } from '@/pages/billing/pending-order-bar'
import { useTranslation } from 'react-i18next'
import { useLanguageSync } from '@/hooks/use-ui-language'

function MainContent({ fullBleed = false, mobile = false }: { fullBleed?: boolean; mobile?: boolean }) {
  const role = useAuthStore((s) => s.user?.role)
  // Menu phụ bên trái bám cùng menu chính đã lọc theo quyền (side-nav).
  const { data: perms } = useMyPermissions()
  const { pathname } = useLocation()
  // Nhãn menu (navForRole) dịch lúc vẽ — useTranslation() để vẽ lại khi đổi ngôn ngữ.
  useTranslation()
  // Hội thoại là ứng dụng chat nhiều cột — cần TRÀN toàn khung, không bị bó trong
  // hộp max-width + padding, và tự quản việc cuộn bên trong từng cột.
  if (fullBleed) {
    return (
      <main className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </main>
    )
  }

  // min-w-0: cột flex mặc định không co dưới min-content — nội dung dài (bảng,
  // markdown...) sẽ đẩy tràn ngang làm cả trang "trượt" sau sidebar ở menu dọc.
  // overflow-x-hidden chặn cuộn ngang toàn trang; phần rộng phải tự cuộn trong
  // khung riêng của nó.
  // Mục có nhiều trang con (Khách hàng, Báo cáo…) → menu phụ bên trái, như SaleSmartly.
  const section = sectionForPath(mobile ? mobileNav(navForRole(role)) : navForRole(role, perms), pathname)
  const main = (
    <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
      <div className={cn('mx-auto max-w-[1400px]', mobile ? 'px-4 py-3' : 'px-6 py-5')}>
        <Outlet />
      </div>
    </main>
  )
  if (!section?.children || subnavPageCount(section) < 2) return main
  // Điện thoại: menu phụ thành hàng chip cuộn ngang ở đầu trang (không có cột trái).
  if (mobile) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <MobileSectionTabs item={section} />
        {main}
      </div>
    )
  }
  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <SectionSubnav item={section} />
      {main}
    </div>
  )
}

export function AppLayout() {
  const location = useLocation()
  const isMobile = useIsMobile()
  // Điện thoại + màn chat chi tiết: khung ứng dụng co theo bàn phím ảo (iOS không tự co) để không mất tiêu đề / ô soạn không lơ lửng.
  useKeyboardViewport(isMobile && isChatDetailPath(location.pathname))
  // Trạng thái kết nối tài khoản kênh cập nhật realtime ở MỌI trang (Kết nối, Hội thoại…).
  useAccountStatusRealtime()
  // Ngôn ngữ giao diện: áp lựa chọn đã lưu trên server / mặc định tổ chức sau khi đăng nhập.
  useLanguageSync()
  // Hội thoại: tràn toàn màn hình, menu chính chỉ chiếm bề rộng cột icon.
  const onConversations = location.pathname.startsWith('/conversations')
  // Trình dựng luồng tự động hoá là canvas kéo thả — cũng cần tràn toàn khung.
  const fullBleed =
    onConversations ||
    location.pathname.startsWith('/automation/flow/') ||
    // Tích hợp, Cài đặt và Cài đặt cá nhân có menu phụ riêng bên trái (kiểu
    // SaleSmartly) — tự lo cuộn bên trong.
    location.pathname.startsWith('/integrations') ||
    location.pathname === '/settings' ||
    location.pathname.startsWith('/account/')

  // Điện thoại (Lark Mobile): không có cột menu / khung nổi — nội dung tràn màn hình, thanh tab nằm dưới;
  // màn chat (/conversations/<id>) chiếm cả màn hình, ẩn thanh tab. h-dvh: bám chiều cao thật khi bàn phím ảo / thanh địa chỉ co giãn.
  if (isMobile) {
    const blocked = desktopOnlyFor(location.pathname)
    return (
      // height: khi bàn phím ảo mở ở màn chat, `--app-vh` = chiều cao vùng nhìn thấy (hooks/use-keyboard-viewport.ts); không thì 100dvh như cũ.
      <div className="app-shell flex h-dvh flex-col bg-background pl-safe pr-safe" style={{ height: 'var(--app-vh, 100dvh)' }}>
        <NotificationCenter />
      <ReleaseNoteDialog />
        <SessionExpiredModal />
        <WelcomeDialog />
        <InstallGuideDialog />
        <StageManagerDialog />
        <StageFieldsDialog />
        <EntryPrompts mobile />
        <ImpersonationBanner />
        <OnboardingBackBar />
        {/* Chức năng chỉ dùng trên máy tính (gõ / lưu đường dẫn) → thẻ giải thích thay cho trang. */}
        {blocked ? <DesktopOnlyNotice area={blocked} /> : <MainContent fullBleed={isMobileFullBleedPath(location.pathname)} mobile />}
        {!isChatDetailPath(location.pathname) && <PendingOrderBar mobile />}
        {!isChatDetailPath(location.pathname) && <MobileTabBar />}
        <OnboardingResumeBar />
        <FeedbackBubble />
      </div>
    )
  }

  // Cột menu (thông báo, tài khoản nằm ở đáy cột) + khung nội dung bo góc nổi
  // trên nền gradient, theo bố cục SaleSmartly. Hội thoại tự chia thành nhiều
  // thẻ riêng nên không bọc trong khung trắng chung.
  return (
    <div className="app-shell flex h-screen flex-col bg-shell">
      {/* Âm báo / thông báo trình duyệt / nháy tab khi có tin mới — xem notification-center. */}
      <NotificationCenter />
      <ReleaseNoteDialog />
      {/* Hết phiên → hộp đăng nhập lại ngay trên trang hiện tại (không văng về /login). */}
      <SessionExpiredModal />
      {/* Vừa đăng ký xong → chào mừng + gói Free đã kích hoạt + nút "Bắt đầu khám phá" (một lần). */}
      <WelcomeDialog />
      {/* Hộp hướng dẫn cài app (mở từ nút "Tải ứng dụng" ở menu cá nhân) + lời mời lúc vào web: xin quyền thông báo → thẻ cài app. */}
      <InstallGuideDialog />
      <StageManagerDialog />
      <StageFieldsDialog />
      <EntryPrompts mobile={false} />
      {/* Bảng lệnh ⌘K / Ctrl+K — tìm trang, cài đặt, thao tác (mở thêm từ kính lúp đầu menu trái). */}
      <CommandPalette />
      <ImpersonationBanner />
      {/* Đơn thanh toán đang chờ / vừa hết hạn — nhắc ở mọi menu (trừ /billing). */}
      <PendingOrderBar />
      {/* overflow-clip (KHÔNG overflow-hidden): bảng con của menu trái (ẩn, absolute) thò xuống quá đáy → khung "hidden" vẫn cuộn được
          bằng script / focus (vd bật AI Trợ lý) → cả giao diện bị đẩy lên. "clip" cắt y hệt nhưng không bao giờ cuộn. */}
      <div className="flex min-h-0 flex-1 overflow-clip">
      <SideNav forceCollapsed={onConversations} />
      <div
        className={cn(
          'my-2 mr-2 flex min-w-0 flex-1 flex-col overflow-hidden',
          !onConversations && 'rounded-xl bg-background shadow-[0_0_0_1px_hsl(var(--border))]',
        )}
      >
        {/* Mở từ trang Bắt đầu (?from=onboarding) → lối về luôn thấy ở đầu trang đích. */}
        <OnboardingBackBar />
        <MainContent fullBleed={fullBleed} />
        </div>
      </div>
      {/* "Tiếp tục thiết lập (x/2)" — chủ tổ chức mới chưa xong / chưa bỏ qua trang Bắt đầu. */}
      <OnboardingResumeBar />
      {/* Máy tính: nút Gửi Feedback nằm trong menu trái; FeedbackBubble chỉ vẽ trên điện thoại. */}
      <FeedbackBubble />
    </div>
  )
}
